import type { Tint } from '../core/viewer.ts';

/* Pure mapping from platform state to what the viewer shows. Absent or unknown stays unknown. */

export interface PlaceState {
  code: string;
  booking: 'free' | 'booked' | 'not_bookable';
  readings: Record<string, { value: number; at: string }>;
}

export function tintFor(state: PlaceState | undefined, selected: boolean): Tint {
  if (selected) return 'selected';
  if (!state) return 'unknown';
  if (state.booking === 'free') return 'free';
  if (state.booking === 'booked') return 'booked';
  return 'neutral';
}

/** Applies a snapshot or a delta; a delta older than the snapshot it follows is dropped. */
export function applyUpdate(
  current: { sequence: number; places: Map<string, PlaceState> },
  update: { sequence: number; places: PlaceState[] },
): { sequence: number; places: Map<string, PlaceState>; changed: string[] } {
  if (update.sequence < current.sequence) return { ...current, changed: [] };
  const places = new Map(current.places);
  for (const place of update.places) places.set(place.code, place);
  return { sequence: update.sequence, places, changed: update.places.map((place) => place.code) };
}

export function describeReadings(readings: PlaceState['readings']): string[] {
  const units: Record<string, string> = { temperature: '°C', co2: 'ppm', humidity: '%', comfort_index: '' };
  return Object.entries(readings).map(
    ([metric, reading]) =>
      `${metric.replace('_', ' ')} ${Math.round(reading.value * 10) / 10}${units[metric] ?? ''}`,
  );
}
